import React, { ChangeEvent, useEffect, useState } from 'react';

import { Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getComparator, Order, stableSort } from 'utils/dataTableUtils';
import EnhancedTableHead from 'components/DataTable/EnhancedTableHead';
import { ColumnDefinition } from 'components/DataTable/DataTableTypes';
import { DataTableRow } from 'components/DataTable/DataTableRow';
import SkeletonTable from 'components/DataTable/TableSkeleton';
import useKeyFilter from 'hooks/useKeyFilter';
import { isNotNullOrUndefined } from 'utils/utils';

export const tableStyles = makeStyles(() => ({
  emptyTable: {
    borderBottom: 'none',
  },
}));

type Props<T> = {
  id?: any;
  headCells: ColumnDefinition<T>[];
  rows: T[];
  ariaLabel?: string;
  borders?: boolean;
  rowsPerPageOptions?: number[];
  onRowClick?: (rowIndex: number) => void;
  selectedRowIndex?: number;
  showPagination?: boolean;
  renderExpanded?: React.ReactNode;
  isLoading?: boolean;
  actionButton?: React.ReactNode;
  emptyMessage?: string;
};

export type ColumnFilters<T> = { [key in keyof T]: string[] };
export type ColumnFilterTuple<T> = { [key in keyof T]: [string] };

export const DataTable = <T,>({
  id,
  rows,
  ariaLabel,
  headCells,
  borders = true,
  rowsPerPageOptions = [10, 50, 100],
  onRowClick = undefined,
  selectedRowIndex,
  showPagination = false,
  renderExpanded,
  isLoading,
  emptyMessage = 'No items found.',
}: Props<T>): React.ReactElement => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('label');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [rowsFiltered, setRowsFiltered] = useState<T[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters<T>[]>({} as any);
  const [mergedRows] = useKeyFilter(rowsFiltered, columnFilters);
  const tableId = id;
  const classes = tableStyles();

  useEffect(() => {
    setRowsFiltered(rows);
    setColumnFilters({} as any);
  }, [JSON.stringify(rows)]);

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleFilter = (selectedFilters: ColumnFilterTuple<T>[]) => {
    if (Object.keys(selectedFilters).length > 0) {
      const propertyName = Object.keys(selectedFilters[0])[0] as keyof T;
      const mappedColumnFilters = selectedFilters.reduce((result, item) => {
        const value = item[propertyName];
        if (value) {
          result[propertyName] = (result[propertyName] || []).concat(value);
        }
        return result;
      }, {} as ColumnFilters<T>);
      setColumnFilters({ ...columnFilters, ...mappedColumnFilters });
    }
  };

  const handleSearch = (searchString: React.SetStateAction<string>, searchKey: keyof T) => {
    const rowsTofilter = rowsFiltered.length > 0 ? rowsFiltered : rows;
    const rowsMatchingFilters = rowsTofilter.filter((row: any) => row[searchKey]?.includes(searchString));
    isNotNullOrUndefined(searchString) ? setRowsFiltered(rowsMatchingFilters) : setRowsFiltered(rows);
  };

  if (isLoading) {
    return <SkeletonTable id={`skeleton-table-${tableId}`} rowCount={5} cellCount={headCells?.length || 5} />;
  }

  return (
    <>
      <TableContainer style={{ borderTop: 'none' }}>
        <Table size="small" aria-label={ariaLabel} id={tableId} data-testid={tableId}>
          <EnhancedTableHead
            data={rows}
            headCells={headCells}
            order={order}
            setOrderBy={setOrderBy}
            setOrder={setOrder}
            orderBy={orderBy}
            handleSort={handleFilter as any}
            handleSearch={handleSearch}
          />
          <TableBody>
            {(!rows || !rows?.length) && (
              <TableRow>
                <TableCell colSpan={headCells.length} className={classes.emptyTable}>
                  <Typography variant="caption"> {emptyMessage} </Typography>
                </TableCell>
              </TableRow>
            )}
            {stableSort(mergedRows, getComparator(order, orderBy))
              .slice(
                showPagination ? page * rowsPerPage : 0,
                showPagination ? page * rowsPerPage + rowsPerPage : mergedRows.length,
              )
              .map((rowFiltered, index: number) => (
                <DataTableRow
                  key={`${tableId}-${index}`}
                  labelId={`${tableId}-${index}`}
                  rowIndex={index}
                  headCells={headCells}
                  row={rowFiltered}
                  renderExpanded={renderExpanded}
                  onRowClick={() => (onRowClick ? onRowClick(index) : null)}
                  selectedRowIndex={selectedRowIndex}
                  borders={borders}
                />
              ))}
            {showPagination && mergedRows?.length > 10 && (
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={rowsPerPageOptions}
                  count={mergedRows?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Entries per page"
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
